import React, { use, useMemo } from "react";
import { useI18n } from "../../hooks/i18n-hooks";
import SimplePageContent from "../../components/SimplePageContent";
import { FIND_WORKER_TIMESHEETS } from "../../graphql/timesheet";
import { useQuery } from "@apollo/client/react";
import Loading from "../../components/Loading";
import TimesheetList from "../../components/TimeSheetList";
import EmptyListInfo from "../../ui_atoms/EmptyListInfo";
import VStack from "../../ui_atoms/VStack";
import { StackScreenProps } from "@react-navigation/stack";
import { ClientWorkersTabParamList } from "../../navigations/client/WorkersNavigation";
import { ProfileType, TimeSheetState } from "../../__generated__/graphql";
import { ProfileSwitcherManagerContext } from "../../contexts/ProfileSwitcherManager";
import { buildErrorHeadband } from "../ScreenCardHeadband";

type Props = StackScreenProps<ClientWorkersTabParamList, "WorkerTimesheetList">;

const WorkerTimesheetList: React.FC<Props> = ({ route }) => {
    const i18n = useI18n();
    const { profileType } = use(ProfileSwitcherManagerContext);
    const statuses = useMemo(
        () =>
            profileType === ProfileType.Worker
                ? [
                      TimeSheetState.Open,
                      TimeSheetState.Accepted,
                      TimeSheetState.Rejected,
                      TimeSheetState.Submitted,
                  ]
                : [TimeSheetState.Accepted, TimeSheetState.Rejected, TimeSheetState.Submitted],
        [profileType],
    );
    const { data, refetch, loading, error } = useQuery(FIND_WORKER_TIMESHEETS, {
        variables: { workerProfileId: route?.params?.workerId || "", statusIn: statuses },
        skip: !route?.params?.workerId,
        fetchPolicy: "cache-and-network",
    });

    const allTimeSheets = data?.findWorkerTimeSheets.elements;
    const count = data?.findWorkerTimeSheets.count;

    return (
        <SimplePageContent
            refresh={refetch}
            headband={
                error
                    ? buildErrorHeadband(i18n.t("screens.user.timesheets.cannot_load_timesheets"))
                    : undefined
            }
        >
            {loading && !allTimeSheets && <Loading />}
            {allTimeSheets && profileType && (
                <TimesheetList profileType={profileType} timeSheets={allTimeSheets} />
            )}
            {count == 0 && (
                <VStack h={1} v={1}>
                    <EmptyListInfo text={i18n.t("timesheet_list.empty")} />
                </VStack>
            )}
        </SimplePageContent>
    );
};

export default WorkerTimesheetList;
